// @booking/lib/presale.ts
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'

import type { BookingConfig, Presale } from '@booking/types/bookingConfig'
import { allPresales } from '@booking/lib/bookingConfig'

const TIME_ZONE = 'Europe/Lisbon'

/**
 * upcoming → open (only the pre-sale link sells its dates) → closed (nothing
 * sells them) → general (every flow sells them).
 */
export type PresaleStatus = 'upcoming' | 'open' | 'closed' | 'general'

type Dated = { dateTime: string }

/** Lisbon calendar day (yyyy-MM-dd) of an instant, e.g. a session start. */
export function lisbonDay(dateTime: string | number | Date): string {
  return formatInTimeZone(dateTime, TIME_ZONE, 'yyyy-MM-dd')
}

/** A Lisbon wall-clock time from the config ('2026-10-14T00:00') as an instant. */
export function lisbonTime(value: string): Date {
  return fromZonedTime(value, TIME_ZONE)
}

/**
 * `?now=2026-10-15T10:00` (Lisbon time) stands in for the current time so each
 * pre-sale state can be checked, on the live site too. On its own it only changes
 * what this site shows: the API keeps its own clock for what it lists and sells,
 * unless the link also carries the API's test key (see presaleTestKey). Outside
 * development it stops working once the last pre-sale's general sale has opened.
 */
export function presaleNowOverride(): Date | null {
  if (typeof window === 'undefined') return null
  const testable =
    process.env.NODE_ENV === 'development' ||
    allPresales().some((presale) => Date.now() < lisbonTime(presale.generalSaleAt).getTime())
  if (!testable) return null
  const value = new URLSearchParams(window.location.search).get('now')
  const date = value ? lisbonTime(value) : null
  return date && !Number.isNaN(date.getTime()) ? date : null
}

export function presaleNow(): Date {
  return presaleNowOverride() ?? new Date()
}

/**
 * `&key=` on a `?now=` link: the API's test key (its PRESALE_TEST_KEY). It goes to
 * the API with the simulated time (services/api), so the API lists and sells as at
 * that time too. The key lives only in the links handed out, never in this site.
 */
export function presaleTestKey(): string | null {
  if (!presaleNowOverride()) return null
  return new URLSearchParams(window.location.search).get('key') || null
}

/** A test link's `now` (and `key`), for links that should keep its simulated time. */
export function presaleTestParams(): Record<string, string> {
  const now = presaleNowOverride()
  if (!now) return {}
  const key = presaleTestKey()
  return { now: formatInTimeZone(now, TIME_ZONE, "yyyy-MM-dd'T'HH:mm"), ...(key ? { key } : {}) }
}

export function presaleStatus(presale: Presale, now: Date): PresaleStatus {
  if (now < lisbonTime(presale.opensAt)) return 'upcoming'
  if (now < lisbonTime(presale.closesAt)) return 'open'
  if (now < lisbonTime(presale.generalSaleAt)) return 'closed'
  return 'general'
}

/** When the status next changes, or null once general sale has opened. */
export function nextPresaleChange(presale: Presale, now: Date): Date | null {
  const changes = [presale.opensAt, presale.closesAt, presale.generalSaleAt].map(lisbonTime)
  return changes.find((change) => change > now) ?? null
}

/** The day a pre-sale flow sells, when it sells just one; its date step then shows no calendar. */
export function presaleSingleDay(config: BookingConfig): string | null {
  const dates = config.presale?.sessionDates
  return dates?.length === 1 ? dates[0] : null
}

/**
 * Whether a flow lists a session. A pre-sale flow lists only its own dates;
 * every other flow holds those dates back until general sale opens.
 */
export function isSessionListed(session: Dated, config: BookingConfig, now: Date): boolean {
  const day = lisbonDay(session.dateTime)
  if (config.presale) return config.presale.sessionDates.includes(day)
  return !allPresales().some(
    (presale) => presale.sessionDates.includes(day) && now < lisbonTime(presale.generalSaleAt),
  )
}

/** Whether any flow is selling a session right now. */
export function isSessionOnSale(session: Dated, now: Date): boolean {
  const day = lisbonDay(session.dateTime)
  return allPresales().every((presale) => {
    if (!presale.sessionDates.includes(day)) return true
    const status = presaleStatus(presale, now)
    return status === 'open' || status === 'general'
  })
}
